import { Component, HostListener, OnInit } from '@angular/core';
import { Validators, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Observable, of, Subscription } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';
import { DEFAULT_LANG, LANGUAGES } from 'src/app/conf/languages';
import { deepLinkMobile } from 'src/app/conf/login';
import { Lang } from 'src/app/model/lang';
import { HelperViewGuestDialogComponent } from 'src/app/modules/help/components/helper-view-guest-dialog/helper-view-guest.dialog.component';
import { AppState } from 'src/app/store/app.reducer';
import * as AuthActions from 'src/app/store/auth/auth.actions';
import { selectConfirmUserAccount, selectLoginMfaCode, selectRegistrationSuccess } from 'src/app/store/auth/auth.selector';
import { selectCurrentLang, selectUser } from 'src/app/store/user/user.selector';
import { PasswordResetModalDialogComponent } from './password-reset-modal/password-reset-modal.dialog.component';
import { CLOUD_STATUS_OK, isCloudInMaintenance } from 'src/app/conf/maintenance';
import { TokenData } from 'src/app/model/api/token-data';
import { AuthService } from 'src/app/services/auth.service';
import { AppComponent } from 'src/app/app.component';
import { StandardDesignParameters } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { User } from 'src/app/model/user';
import * as AnalyticsActions from 'src/app/store/analytics/analytics.actions';
import { normalizeEmailControl } from 'src/app/modules/shared/utils/strip-email-spaces';
import { selectCatchInstantLoginEve, selectCatchLoginEve } from 'src/app/store/analytics/analytics.selector';
import { selectStatusData } from 'src/app/store/task/task.selector';
import { StatusData } from 'src/app/model/api/data-rest';
import { LoginCodeMfaDialogComponent } from './login-code-mfa/login-code-mfa.dialog.component';
import { environment } from 'src/environments/environment';

@Component({
	selector: 'app-login',
	templateUrl: './login.component.html',
	providers: [DialogService, ConfirmationService],
	styles: `
		img {
			user-select: none;
			-webkit-user-select: none;
			-webkit-user-drag: none;
			user-drag: none;
		}
	`
})

export class LoginComponent implements OnInit {
	confirmUserAccount$: Observable<number | undefined>;
	confirmUserAccountSub: Subscription | undefined;
	registrationSuccess$: Observable<boolean | undefined>;
	registrationSuccessSub: Subscription | undefined;

	statusMaintanance$: Observable<StatusData | undefined>;
	statusMaintananceSub: Subscription | undefined;

	currentUser: User;
	currentUser$: Observable<User | undefined>;
	currentUserSub: Subscription | undefined;

	confirmUserAccount: number;

	loginForm: FormGroup;
	showPassword = false;

	currentLang$: Observable<Lang> = new Observable();
	currentLang: Lang = DEFAULT_LANG;
	languages = LANGUAGES;

	maintenance: number;

	// Design
	screenWidth: any;
	screenHeight: any;

	ref: DynamicDialogRef | undefined;
	acceptButtonStyle: string;
	rejectButtonStyle: string;
	rejectButtonVisible: boolean;
	tokenData: TokenData;

	analytics = this.app.initFirebase();

	catchLoginEve: boolean;
	catchLoginEve$: Observable<boolean | undefined>;
	catchLoginEveSub: Subscription | undefined;

	catchInstantLoginEve: boolean;
	catchInstantLoginEve$: Observable<boolean | undefined>;
	catchInstantLoginEveSub: Subscription | undefined;
	loginMfaCode$: Observable<number | undefined>;
	loginMfaCodeSub: Subscription | undefined;

	constructor(
		private store: Store<AppState>,
		public dialogService: DialogService,
		private translate: TranslateService,
		public confirmationService: ConfirmationService,
		private authService: AuthService,
		private route: ActivatedRoute,
		public app: AppComponent,
		private router: Router,
	) {
		this.loginForm = new FormGroup({
			'username': new FormControl('', Validators.required),
			'password': new FormControl('', Validators.required)
		});
		this.initSelectors();
	}

	ngOnInit(): void {
		this.redirectFromBetaToOffiacial();
		this.currentLang$.subscribe((lang: Lang) => {
			this.currentLang = lang;
		});
		this.statusMaintanance$.subscribe(statusData => {
			if (statusData !== undefined && statusData !== null && statusData.Status !== undefined && statusData.Status !== null) {
				this.maintenance = statusData.Status;
			} else {
				this.maintenance = CLOUD_STATUS_OK;
			}
		});
		// this.statusMaintanance$.subscribe(statusData => {
		// 	this.maintenance = isCloudInMaintenance(statusData?.Status)
		// 		? statusData.Status
		// 		: CLOUD_STATUS_OK;
		// });
		this.screenWidth = window.innerWidth;
		this.screenHeight = window.innerHeight;

		this.subscribeAll();

		const token = this.route.snapshot.queryParamMap.get('br');
		const expires = this.route.snapshot.queryParamMap.get('ex');
		if (token !== undefined && expires !== undefined && token !== null && expires !== null) {
			// LOGIN WITH TOKEN
			this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'login_instant_token', params: { page_title: 'Login' } }));
			this.tokenData = {
				access_token: token,
				expires_in: Number(expires),
				refresh_token: '',
				token_type: 'bearer',
				error: null,
			};
			this.store.dispatch(AuthActions.getMfaTokenSuccess({
				tokenData: this.tokenData,
				userId: this.authService.getTokenUserId(this.tokenData.access_token),
				redirect: true
			}));
		}

		const verificationData = this.route.snapshot.queryParams['data'];
		const id = this.route.snapshot.queryParams['id'];

		if (verificationData && id) {
			this.store.dispatch(AuthActions.confirmEmail({ id: +id, data: verificationData }));
		}

		this.onResize();

		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventNoAuth({ eventName: 'custom_page_view', params: { page_title: 'Login' } }));
		}, 2000);
	}

	initSelectors(): void {
		// Store selectors
		this.confirmUserAccount$ = this.store.select(selectConfirmUserAccount);
		this.registrationSuccess$ = this.store.select(selectRegistrationSuccess);
		this.currentUser$ = this.store.select(selectUser);
		this.catchLoginEve$ = this.store.select(selectCatchLoginEve);
		this.currentLang$ = this.store.select(selectCurrentLang);
		this.statusMaintanance$ = this.store.select(selectStatusData);
		this.catchInstantLoginEve$ = this.store.select(selectCatchInstantLoginEve);
		this.loginMfaCode$ = this.store.select(selectLoginMfaCode);
	}

	subscribeAll(): void {
		const appId = this.route.snapshot.queryParams['appid'];
		this.confirmUserAccountSub = this.confirmUserAccount$?.subscribe(confirmUserAccount => {
			this.confirmUserAccount = confirmUserAccount;
			if (confirmUserAccount === 2) { /*Success*/
				let browserDevice = 'PC';
				if (navigator.userAgent.toLowerCase().includes('iphone') || navigator.userAgent.toLowerCase().includes('ipad')) {
					browserDevice = 'IOS';
				} else if (navigator.userAgent.toLowerCase().includes('android')) {
					browserDevice = 'ANDROID';
				}
				if (!appId || (appId !== 'app-thermo2' && appId !== 'app-cameaccess')) { // Attivo deep link per Thermo2 e CAMEAccess
					browserDevice = 'PC';
				}
				switch (browserDevice) {
					case 'IOS': {
						this.redirectWebPage(appId, browserDevice);
						break;
					}
					case 'ANDROID': {
						this.redirectWebPage(appId, browserDevice);
						break;
					}
					default: {
						this.confirmUser();
						break;
					}
				}
			} else if (confirmUserAccount === 1) { /*Error*/
				this.errorConfirmUser();
			}
			this.store.dispatch(AuthActions.confirmEmailReset());
		});

		this.registrationSuccessSub = this.registrationSuccess$?.subscribe(registrationSuccess => {
			if (registrationSuccess) {
				this.confirmRegistrationDialog();
			}
		});
		this.catchLoginEveSub = this.catchLoginEve$.subscribe((catchLoginEve: boolean) => {
			this.catchLoginEve = catchLoginEve;
		});
		this.catchInstantLoginEveSub = this.catchInstantLoginEve$.subscribe((catchInstantLoginEve: boolean) => {
			this.catchInstantLoginEve = catchInstantLoginEve;
		});
		this.loginMfaCodeSub = this.loginMfaCode$.subscribe(loginMfaCode => {
			if (loginMfaCode === 1) {
				console.log('loading');
			}
			if (loginMfaCode === 2) { // SHOW DIALOG
				console.log('loading');
				this.showDialogMfa();
			}
		});
	}

	confirmRegistrationDialog(): void {
		this.acceptButtonStyle = "p-button-secondary";
		this.rejectButtonVisible = false;
		this.openTranslatedConfirm({
			headerKey: 'CONFIRM_REGISTRATION',
			messageKey: 'NEW_CONFIRM_REGISTRATION_CHECK_MAIL',
			acceptKey: 'CLOSE',
			rejectKey: 'CANCEL',
		});
	}

	getCurrentLanguage(): string {
		return this.currentLang.name;
	}

	login(): void {
		normalizeEmailControl(this.loginForm.get('username'));
		//this.showDialogMfa();
		this.store.dispatch(AuthActions.authMfaCode({
			username: this.loginForm.value.username,
			password: this.loginForm.value.password
		}));
		this.currentUserSub = this.currentUser$?.subscribe(currentUser => {
			this.currentUser = currentUser;
		});
		if (this.catchInstantLoginEve) {
			this.store.dispatch(AnalyticsActions.eventNewAction({ eventName: 'login_instant_catch', params: { page_title: 'Login' } }));
		}
		setTimeout(() => {
			if (this.catchLoginEve) {
				this.store.dispatch(AnalyticsActions.eventLogin({ eventName: 'login', params: { page_title: 'Login' } }));
			}
		}, 3000);
	}

	signNewUser() {
		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventNoAuth({ eventName: 'signup_started', params: { page_title: 'Registration' } }));
		}, 2000);
	}

	signNewTechnician() {
		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventNoAuth({ eventName: 'signup_started', params: { page_title: 'Registration' } }));
		}, 2000);
	}

	redirectWebPage(appId: string, deviceType: string): void {
		this.acceptButtonStyle = "p-button-primary";
		this.rejectButtonStyle = "p-button-secondary";
		this.rejectButtonVisible = true;
		this.openTranslatedConfirm({
			headerKey: 'CONFIRM_REGISTRATION',
			messageKey: 'EMAIL_CONFIRMED',
			extraMessageKeys: ['REDIRECT_TO'],
			acceptKey: 'CONFIRM',
			rejectKey: 'CANCEL',
			messageSuffix: () => appId + ' DeviceType: ' + deviceType,
			accept: () => {
				const deepLinkKey = appId + '_' + deviceType;
				window.location.href = deepLinkMobile.get(deepLinkKey.toUpperCase());
			},
		});
	}

	confirmUser(): void {
		this.acceptButtonStyle = "p-button-primary";
		this.rejectButtonVisible = false;
		this.openTranslatedConfirm({
			headerKey: 'CONFIRM_REGISTRATION',
			messageKey: 'EMAIL_CONFIRMED',
			acceptKey: 'CONFIRM',
			rejectKey: 'CANCEL',
		});
	}

	errorConfirmUser(): void {
		this.acceptButtonStyle = "p-button-primary";
		this.rejectButtonVisible = false;
		this.openTranslatedConfirm({
			headerKey: 'CONFIRM_REGISTRATION',
			messageKey: 'EMAIL_CONFIRMED_ERROR',
			acceptKey: 'CONFIRM',
			rejectKey: 'CANCEL',
		});
	}

	private openTranslatedConfirm(options: {
		headerKey: string;
		messageKey: string;
		acceptKey: string;
		rejectKey: string;
		extraMessageKeys?: string[];
		messageSuffix?: () => string;
		accept?: () => void;
		reject?: () => void;
	}): void {
		const keys = [
			options.headerKey,
			options.messageKey,
			options.acceptKey,
			options.rejectKey,
			...(options.extraMessageKeys ?? []),
		];
		const langReady$ = this.translate.currentLang
			? of(this.translate.currentLang)
			: this.translate.onLangChange.pipe(take(1), map(event => event.lang));

		langReady$.pipe(
			switchMap(() => this.translate.get(keys)),
		).subscribe((translations: Record<string, string>) => {
			const extra = (options.extraMessageKeys ?? [])
				.map(key => translations[key])
				.join('');
			this.confirmationService.confirm({
				header: translations[options.headerKey],
				message: translations[options.messageKey] + extra + (options.messageSuffix?.() ?? ''),
				acceptLabel: translations[options.acceptKey],
				rejectLabel: translations[options.rejectKey],
				icon: 'pi cm-alert',
				accept: options.accept ?? (() => { }),
				reject: options.reject ?? (() => { }),
			});
		});
	}

	showDialogMfa(): void {
		this.ref = this.dialogService.open(LoginCodeMfaDialogComponent, {
			header: this.translate.instant('MFA_NECESSARY'),
			width: '50vw',
			breakpoints: { '1120px': '95vw' },
			contentStyle: { overflow: 'auto' },
			data: {
				username: this.loginForm.value.username,
				password: this.loginForm.value.password
			}
		});
	}

	resetPasswordModal(): void {
		this.ref = this.dialogService.open(PasswordResetModalDialogComponent, {
			header: this.translate.instant('RESET_PASSWORD'),
			width: '50vw',
			breakpoints: { '1120px': '95vw' },
			contentStyle: { overflow: 'auto' },
		});
	}

	helpDialog(): void {
		this.ref = this.dialogService.open(HelperViewGuestDialogComponent, {
			header: this.translate.instant('HELPER'),
			width: '80vw',
			breakpoints: { '1120px': '95vw' },
			contentStyle: { overflow: 'auto' },
		});
		setTimeout(() => {
			this.store.dispatch(AnalyticsActions.eventNoAuth({ eventName: 'need_help', params: { page_title: 'Help' } }));
		}, 2000);
	}

	getMenuTheme(): string {
		return this.app.menuTheme;
	}

	redirectFromBetaToOffiacial(): void {
		if (window.location.href.includes('beta.cameconnect.net')) {
			console.log('redirect');
			window.open(environment.frontEnd + '/login', '_self');
		}
	}

	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
		this.screenHeight = window.innerHeight;
	}

	openNewTab(url: string): void {
		window.open(url, '_blank');
	}

	onLargeDesktop(): string {
		return (this.screenHeight >= StandardDesignParameters.LARGE_DESKTOP) && (this.screenWidth >= StandardDesignParameters.FOOTER_EXTENDED_MIN_WIDTH) ? "col-12 flex" : "";
	}

	fixedFooter(): string {
		return (this.screenHeight >= StandardDesignParameters.LG_HEIGHT) && (this.screenWidth >= StandardDesignParameters.FOOTER_EXTENDED_MIN_WIDTH) ? "fixed bottom-0 min-w-screen" : "";
	}

	minHeightScreen(): string {
		return (this.screenWidth <= StandardDesignParameters.FOOTER_EXTENDED_MIN_WIDTH) ? "" : "min-h-screen"
	}
}
